import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {warehousesTableRowsMock} from "../../utils/mock/table-shipments";
import {sendFormToLocalStorage} from "../../utils/LocalStorageHelper";
import {setFormModalOpen} from "../forms/actions";
import Warehouse from "../../pages/Warehouse";
import warehousesService from "../../api/services/warehousesService";
import axios from "axios";
import store from "../store";
import {useSelector} from "react-redux";
import {useAppSelector} from "../hooks";
import {setTemporaryNotifications} from "../notifications/actions";
import {defaultNotificationCreator} from "../../templates/Notifications";
import {t} from "i18next";
import {IColumnData, IRowData, tableShipmentsLimit} from "../../constants/Table/interfaces";

interface IWarehousesReducer {
    rowData: IRowData[];
    columnData: IColumnData[];
    editingRow: IRowData | null;
    currentPage: number;
    currentLimit: tableShipmentsLimit;
    isNeedUpdateData: boolean;
    isWarehouseFormModalOpen: boolean;
}

const initialState: IWarehousesReducer = {
    rowData: [],
    columnData: [],
    editingRow: null,
    currentPage: 1,
    currentLimit: 10,
    isNeedUpdateData: false,
    isWarehouseFormModalOpen: false
}

export const addNewWarehouse = createAsyncThunk(
    'warehouses/createNew',
    async (data:any) => {
        try {
            console.log('addNewWarehouse', data);
            const res = await warehousesService.createNewWarehouse(data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsWarehouseFormModalOpen(false))
            store.dispatch(setIsNeedUpdateData(true))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Склад успешно добавлен",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось добавить новый склад",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const updateWarehouse = createAsyncThunk(
    'warehouses/update',
    async (payload:any) => {
        try {
            const uuid = payload.uuid
            const data = payload.data
            console.log(`updateWarehouse ${uuid}`, data);
            const res = await warehousesService.updateWarehouse(uuid, data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsWarehouseFormModalOpen(false))
            store.dispatch(setIsNeedUpdateData(true))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Склад успешно обновлен",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось обновить склад",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

const warehousesStateSlice = createSlice({
    name: 'warehouses',
    initialState,
    reducers: {
        /**
         * @description Removing one element from rowData
         * @param state
         * @param action
         */
        removeRowDataElement(state, action: PayloadAction<{ id: string }>) {
            state.rowData = state.rowData.filter(
                (element) => element.id !== action.payload.id
            );
        },
        /**
         * @description Editing one element in rowData
         * @param state
         * @param action
         */
        editRowDataElement(state, action: PayloadAction<IRowData>) {
            state.rowData = state.rowData.map((element) =>
                element.id !== action.payload.id ? element : action.payload
            );
        },
        /**
         * @description Setting rows
         * @param state
         * @param action
         */
        setRowData(state, action: PayloadAction<IRowData[]>) {
            state.rowData = action.payload;
        },
        /**
         * @description Setting current row for editing
         * @param state
         * @param action
         */
        setEditingRow(state, action: PayloadAction<IRowData>) {
            state.editingRow = action.payload;
        },
        /**
         * @description Reset editingRow field
         * @param state
         */
        resetEditingRow(state) {
            state.editingRow = null;
        },
        /**
         * @description Set current pagination page
         * @param state
         * @param action
         */
        setCurrentPage(state, action: PayloadAction<number>) {
            state.currentPage = action.payload;
        },
        /**
         * @description Set current pagination limit of elements at page
         * @param state
         * @param action
         */
        setCurrentLimit(state, action: PayloadAction<tableShipmentsLimit>) {
            state.currentLimit = action.payload;
        },
        /**
         * @description Set flag that means is data must be updated
         * @param state
         * @param action
         */
        setIsNeedUpdateData(state, action: PayloadAction<boolean>) {
            state.isNeedUpdateData = action.payload;
        },
        /**
         * @description Set flag that open/close warehouse form modal
         * @param state
         * @param action
         */
        setIsWarehouseFormModalOpen(state, action: PayloadAction<boolean>) {
            state.isWarehouseFormModalOpen = action.payload;
        },
    },
});

const warehouses = warehousesStateSlice.reducer;

export const {
    removeRowDataElement,
    editRowDataElement,
    setEditingRow,
    resetEditingRow,
    setIsNeedUpdateData,
    setIsWarehouseFormModalOpen,
    setRowData
} = warehousesStateSlice.actions;

export default warehouses